import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { CartlengthService } from 'src/app/services/cartlength.service';
import { Router } from '@angular/router';
import { AgeGateService } from 'src/app/services/age-gate.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-start',
  templateUrl: './start.component.html',
  styleUrls: ['./start.component.css']
})
export class StartComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('floatingCart') floatingCart!: ElementRef;
  private listeners: (() => void)[] = [];

  private isDragging = false;
  private offsetX = 0;
  private offsetY = 0;
  private screenWidth = window?.innerWidth;
  private screenHeight = window?.innerHeight;
  private cartWidth = 50;
  private cartHeight = 50;
  isBrowser = typeof window !== 'undefined';
  private subscriptions: Subscription[] = [];

  cartLength: number = 0;

  constructor(
    private renderer: Renderer2,
    private cartLengthService: CartlengthService,
    private router: Router,
    public ageGate: AgeGateService


  ) { }

  ngOnInit(): void {
    if (!this.isBrowser) return;

    this.loadInitialCartCount();

    const sub = this.cartLengthService.cartLengthUpdate.subscribe((count: number) => {
      this.cartLength = count;
      if (this.cartLength === 0) {
        localStorage.setItem("loggedInCart", "0");
      }
    });

    this.subscriptions.push(sub);
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    const cart = this.floatingCart?.nativeElement;
    if (!cart) return;

    // Mouse events
    this.listeners.push(
      this.renderer.listen(cart, 'mousedown', (event: MouseEvent) => this.startDragging(event))
    );

    this.listeners.push(
      this.renderer.listen(document, 'mousemove', (event: MouseEvent) => this.drag(event))
    );

    this.listeners.push(
      this.renderer.listen(document, 'mouseup', () => this.stopDragging())
    );

    // Touch events
    this.listeners.push(
      this.renderer.listen(cart, 'touchstart', (event: TouchEvent) => this.startTouchDragging(event))
    );

    this.listeners.push(
      this.renderer.listen(document, 'touchmove', (event: TouchEvent) => this.touchDrag(event))
    );

    this.listeners.push(
      this.renderer.listen(document, 'touchend', () => this.stopDragging())
    );

    // Window resize (Renderer safe)
    this.listeners.push(
      this.renderer.listen('window', 'resize', () => {
        this.screenWidth = window.innerWidth;
        this.screenHeight = window.innerHeight;
      })
    );

    // Route change
    const routeSub = this.router.events.subscribe(() => {
      const hiddenRoutes = ['/login', '/register'];

      if (hiddenRoutes.includes(this.router.url)) {
        this.renderer.setStyle(cart, 'display', 'none');
      } else {
        this.renderer.setStyle(cart, 'display', 'block');
      }
    });

    this.subscriptions.push(routeSub);
  }

  // ------------------------
  // CLEANUP (VERY IMPORTANT)
  // ------------------------
  ngOnDestroy(): void {
    this.listeners.forEach(unlisten => unlisten());
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  loadInitialCartCount(): void {
    if (!this.isBrowser) return;

    const storedCartCount = localStorage.getItem("loggedInCart");
    this.cartLength = storedCartCount ? Number(storedCartCount) : 0;
  }

  startDragging(event: MouseEvent): void {
    if (!this.floatingCart) return;

    const rect = this.floatingCart.nativeElement.getBoundingClientRect();

    this.isDragging = true;
    this.offsetX = event.clientX - rect.left;
    this.offsetY = event.clientY - rect.top;
  }
  drag(event: MouseEvent): void {
    if (!this.isDragging || !this.floatingCart) return;

    this.moveCart(event.clientX - this.offsetX, event.clientY - this.offsetY);
  }

  startTouchDragging(event: TouchEvent): void {
    if (!this.floatingCart) return;

    const touch = event.touches[0];
    const rect = this.floatingCart.nativeElement.getBoundingClientRect();

    this.isDragging = true;
    this.offsetX = touch.clientX - rect.left;
    this.offsetY = touch.clientY - rect.top;
  }
  touchDrag(event: TouchEvent): void {
    if (!this.isDragging || !this.floatingCart) return;

    const touch = event.touches[0];
    this.moveCart(touch.clientX - this.offsetX, touch.clientY - this.offsetY);
  }

  private moveCart(x: number, y: number): void {
    if (!this.floatingCart) return;

    const cart = this.floatingCart.nativeElement;

    const rect = cart.getBoundingClientRect();
    this.cartWidth = rect.width;
    this.cartHeight = rect.height;

    this.screenWidth = window.innerWidth;
    this.screenHeight = window.innerHeight;

    const minX = 0;
    const minY = 0;
    const maxX = this.screenWidth - this.cartWidth;
    const maxY = this.screenHeight - this.cartHeight;

    x = Math.max(minX, Math.min(x, maxX));
    y = Math.max(minY, Math.min(y, maxY));

    this.renderer.setStyle(cart, 'left', `${x}px`);
    this.renderer.setStyle(cart, 'top', `${y}px`);
    this.renderer.setStyle(cart, 'position', 'fixed');
  }
  stopDragging(): void {
    this.isDragging = false;
  }

  scroll() {
    window.scrollTo(0, 0);
  }

  focusMainContent(event: Event): void {
    event.preventDefault();
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
    }
  }
}
